<template>
  <main>
    <SCMTransactionsFilterComponent
      :loading="loadingRelatedData || loadingTable"
      :subInventories="subInventories"
      :transactionsTypes="transactionsTypes"
    />

    <v-container>
      <v-toolbar flat class="main-header mb-6">
        <v-toolbar-title class="main-header__title">
          <h1>{{ $t("transactions") }}</h1>
        </v-toolbar-title>

        <v-spacer></v-spacer>
        <v-toolbar-items class="main-header__contents">
          <v-btn
            class="my-auto mx-2 radius-50"
            color="primary"
            icon
            :title="$t('changeLocale')"
            @click="changeLocale"
          >
            <v-icon> mdi-web </v-icon>
          </v-btn>
          <v-btn
            v-if="canAdd"
            class="my-auto mx-1 radius-12"
            color="success"
            @click="showAddDialog = true"
          >
            <v-icon>mdi-plus-circle</v-icon>
            <span class="mx-2"> {{ $t("button.add_single") }} </span>
          </v-btn>
          <v-btn
            to="/scm/bulk-transactions"
            class="my-auto mx-1 radius-12"
            color="primary"
          >
            <v-icon>mdi-plus-circle</v-icon>
            <span class="mx-2"> {{ $t("button.add_bulk") }} </span>
          </v-btn>
          <download-excel :data="scmTransactions" :fields="json_fields">
            <v-btn outlined color="red" large class="mt-3"
              ><v-icon class="mr-3">mdi-cloud-download</v-icon>Export</v-btn
            >
          </download-excel>
        </v-toolbar-items>
      </v-toolbar>
      <hr class="mb-6" />

      <v-data-table
        :headers="scmTransactionsTableHeaders"
        :loading="loadingTable"
        :items="scmTransactions"
        :mobile-breakpoint="0"
        :items-per-page="200"
        hide-default-footer
        class="table"
      >
        <template v-slot:item="{ item }">
          <tr class="table__row">
            <td class="table__cell text-center">
              <span> {{ (item.item && item.item.code) || "-" }} </span>
            </td>

            <td class="table__cell name text-center">
              <span> {{ (item.item && item.item.name) || "-" }} </span>
            </td>

            <td class="table__cell text-center">
              <span> {{ (item.type && item.type.name) || "-" }} </span>
            </td>

            <td class="table__cell text-center">
              <span> {{ item.qty || "-" }} </span>
            </td>

            <td class="table__cell text-center">
              <span>
                {{ (item.inventory && item.inventory.name) || "-" }}
              </span>
            </td>

            <td class="table__cell text-center">
              <span> {{ item.locator_name || "-" }} </span>
            </td>

            <td class="table__cell text-center">
              <span> {{ (item.date && item.date.date) || "-" }} </span>
            </td>
            <td class="table__cell text-center">
              <span>
                {{ (item.reference_number && item.reference_number) || "---" }}
              </span>
            </td>

            <td class="table__cell text-center">
              <read-more
                more-str="more"
                less-str="less"
                :text="item.description || '---'"
                :max-chars="20"
                class="mb-0 major-description m-b-0"
              ></read-more>
            </td>
          </tr>
        </template>
      </v-data-table>
      <!-- pagination -->
      <div class="text-xs-center pt-2">
        <v-pagination
          v-if="pagination.last_page > 1"
          v-model="pagination.current_page"
          :length="pagination.last_page"
          :total-visible="20"
          @input="handlePagination"
        ></v-pagination>
      </div>
      <!-- pagination -->
    </v-container>

    <SCMTransactionsAddDialogComponent
      ref="dialog_ref"
      :scmTransactionInfo="scmTransactionInfo"
      :subInventories="activeSubInventories"
      :transactionsTypes="transactionsTypes"
      :loading="loadingAction"
      :loadingActionAdd="loadingActionAdd"
      :listAdded="listAdded"
      :existed="existed"
      @saveSCMTransaction="saveSCMTransaction"
      @saveAndAdd="saveAndAdd"
      @resetTransactionInfo="resetTransactionInfo"
      v-model="showAddDialog"
    >
      <v-alert v-if="globalServerErrors !== null" dense outlined type="error">
        <p
          class="mb-0"
          v-for="globalErr in globalServerErrors"
          :key="globalErr"
        >
          {{ globalErr[0] }}
        </p>
      </v-alert>
    </SCMTransactionsAddDialogComponent>
  </main>
</template>

<script>
import i18n from "@/lang";
import { mapGetters } from "vuex";
import { IndexData, StoreData } from "@/helpers/apiMethods";
import { isEqual } from "lodash";
import SCMTransactionsAddDialogComponent from "./components/add-dialog.component";
import SCMTransactionsFilterComponent from "./components/scmTransactionsFilter.component";

export default {
  components: {
    SCMTransactionsAddDialogComponent,
    SCMTransactionsFilterComponent,
  },
  data() {
    return {
      json_fields: {
        code: {
          field: "item.code",
        },
        name: {
          field: "item.name",
        },
        "Transaction Type": {
          field: "type.name",
        },
        "Transaction Quantity": {
          field: "qty",
        },
        "sub Inventory": {
          field: "inventory.name",
        },
        locator: {
          field: "locator_name",
        },
        "Transaction Date": {
          field: "date.date",
        },

        "Reference Number": {
          field: "reference_number",
        },
        Description: {
          field: "description",
        },
      },
      scmTransactions: [],

      subInventories: [],
      activeSubInventories: [],
      transactionsTypes: [],
      listAdded: [],
      pagination: {},
      queries: {},
      loadingTable: false,
      loadingRelatedData: false,
      loadingAction: false,
      loadingActionAdd: false,
      showAddDialog: false,
      existed: false,
      scmTransactionInfo: {
        item: "",
        transaction_type: "",
        date: "",
        inventory: "",
        to_inventory: "",
        locator_type: "",
        locator: "",
        to_locator_type: "",
        to_locator: "",
        reference_number: "",
        description: "",
        qty: "",
      },
      globalServerErrors: null,
    };
  },
  async mounted() {
    await this.indexSCMTransactionsRelatedData();
  },
  computed: {
    ...mapGetters({
      permissions: "permissions",
    }),
    canAdd() {
      return this.permissions.includes(
        "store_api_admin-panel_scm_transactions"
      );
    },
    scmTransactionsTableHeaders() {
      return [
        {
          text: this.$t("table.itemCode"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.name"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.transaction_type"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.transaction_qty"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.sub_inventory"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.locator"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.transaction_date"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.reference_number"),
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("table.description"),
          align: "center",
          sortable: false,
        },
      ];
    },
  },
  methods: {
    changeLocale() {
      i18n.locale = i18n.locale === "en" ? "ar" : "en";
    },
    indexSCMTransactions(query) {
      this.loadingTable = true;
      IndexData({ reqName: "scm/transactions", query })
        .then((res) => {
          const { data, meta } = res.data;
          this.scmTransactions = data;
          this.pagination = meta;
        })
        .catch(() => {
          console.log();
        })
        .finally(() => {
          this.loadingTable = false;
        });
    },
    async indexSCMTransactionsRelatedData() {
      try {
        this.loadingRelatedData = true;
        const [subInventoriesRes, transactionsTypesRes] = await Promise.all([
          IndexData({ reqName: "scm/inventories" }),
          IndexData({ reqName: "scm/transaction-types" }),
        ]);

        this.subInventories =
          subInventoriesRes && subInventoriesRes.data
            ? subInventoriesRes.data.data.map((subInventor) => ({
                ...subInventor,
                id: subInventor.id.toString(),
              }))
            : [];
        this.activeSubInventories = this.subInventories.filter(
          (inv) => inv.active
        );

        this.transactionsTypes =
          transactionsTypesRes && transactionsTypesRes.data
            ? transactionsTypesRes.data.data.map((transactionsType) => ({
                ...transactionsType,
                id: transactionsType.id.toString(),
              }))
            : [];
      } catch (err) {
        console.log(err);
      } finally {
        this.loadingRelatedData = false;
      }
    },
    handlePagination(page) {
      this.queries = { ...this.queries, page };
      if (!isEqual(this.queries, this.$route.query)) {
        this.$router.push({ query: this.queries });
      }
    },
    saveSCMTransaction() {
      const reqData = this.scmTransactionInfo;
      if (reqData.locator_type === "none") {
        delete reqData.locator_type;
        delete reqData.locator;
      }
      if (!reqData.to_locator || reqData.to_locator_type === "none") {
        delete reqData.to_locator_type;
        delete reqData.to_locator;
      }
      reqData.to_inventory || delete reqData.to_inventory;
      const dateFormat = "YYYY-MM-DD HH:mm:ss";
      this.scmTransactionInfo.date = this.$moment(
        this.scmTransactionInfo.date
      ).format(dateFormat);

      this.loadingAction = true;
      StoreData({ reqName: "scm/transactions", data: reqData })
        .then(() => {
          this.globalServerErrors = null;
          this.showAddDialog = false;
          this.indexSCMTransactions(this.$route.query);
        })
        .catch((err) => {
          this.globalServerErrors = err.response.data.errors;
          this.loadingAction = false;
          this.scrollTop();
        })
        .finally(() => {
          this.loadingAction = false;
        });
    },
    saveAndAdd() {
      const reqData = this.scmTransactionInfo;

      if (reqData.locator_type === "none") {
        delete reqData.locator_type;
        delete reqData.locator;
      }
      if (!reqData.to_locator || reqData.to_locator_type === "none") {
        delete reqData.to_locator_type;
        delete reqData.to_locator;
      }
      reqData.to_inventory || delete reqData.to_inventory;
      const dateFormat = "YYYY-MM-DD HH:mm:ss";
      this.scmTransactionInfo.date = this.$moment(
        this.scmTransactionInfo.date
      ).format(dateFormat);
      this.loadingActionAdd = true;
      StoreData({ reqName: "scm/transactions", data: reqData })
        .then((res) => {
          this.listAdded.push(res.data.transactions[0]);
          this.existed = true;
          this.scmTransactionInfo.item = "";
          this.scmTransactionInfo.code = "";
          this.scmTransactionInfo.name = "";
          this.scmTransactionInfo.qty = "";
          this.$refs.dialog_ref.categorySelected = "";
          if (res.data.transactions[0].type.id != 3) {
            if (res.data.transactions[0].locator_type == null) {
              this.scmTransactionInfo.locator_type = "none";
            }
          }
          if (res.data.transactions[1].type.id == 3) {
            if (res.data.transactions[1].locator_type == null) {
              this.scmTransactionInfo.locator_type = "none";
            }
            this.listAdded.push(res.data.transactions[1]);
          }
          this.globalServerErrors = null;
          if (this.showAddDialog == false) {
            this.indexSCMTransactions(this.$route.query);
            this.existed = false;
            this.listAdded = [];
          }
        })
        .catch((err) => {
          this.globalServerErrors = err.response.data.errors;
          this.loadingActionAdd = false;
          this.scrollTop();
        })
        .finally(() => {
          this.loadingActionAdd = false;
        });
    },
    scrollTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    resetTransactionInfo() {
      this.scmTransactionInfo = {
        item: "",
        transaction_type: "",
        date: "",
        inventory: "",
        to_inventory: "",
        locator_type: "",
        locator: "",
        to_locator_type: "",
        to_locator: "",
        reference_number: "",
        description: "",
        qty: "",
      };
      this.globalServerErrors = null;
      this.existed = false;
      this.listAdded = [];
    },
  },
  watch: {
    $route: {
      handler(route) {
        const { query } = route;
        this.queries = query;
        this.indexSCMTransactions(query);
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.v-data-table .table__row td i.mdi-eye {
  color: #212121 !important;
}
</style>
